import { usePautaDoComite } from '../../api/queries/cab'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../components/Estados'
import { IconeComite } from '../../components/Icones'
import { ROTULO_STATUS, TEXTOS } from '../../lib/glossario'
import type { StatusGmud } from '../../types/gmud'
import { JanelaDeImplantacao } from './JanelaDeImplantacao'
import { LinhaDaPauta } from './LinhaDaPauta'

/** A mesma ordem de `PautaDoComiteService.ORDEM_NA_REUNIAO`: mesa, fila, devolvidas. */
const ORDEM_NA_REUNIAO: StatusGmud[] = ['EM_AVALIACAO_CAB', 'SOLICITADA', 'AGUARDANDO_AJUSTES']

/**
 * A fila de trabalho do comitê.
 *
 * **O que faltava não era o rito — era a porta de entrada.** Pela revisão de escopo de 19/08, a
 * reunião do CAB acontece no escritório: pauta, presença e ata são presenciais e não entram no
 * sistema. O que o sistema guarda é a análise. Mas até aqui o presidente e os membros não tinham
 * tela nenhuma: para decidir, precisavam achar cada solicitação na lista geral, e o material de
 * apoio — parecer de IA, revisão de código do Copilot — só aparecia DEPOIS de abrir. Numa reunião
 * de uma hora com seis itens, descobrir o que existe custava a reunião inteira.
 *
 * **Esta tela não decide.** Não há aqui nenhum botão que mude estado: cada item leva à solicitação,
 * onde a decisão é registrada pelo presidente com fundamentação obrigatória, pela mesma transição
 * de sempre. Aprovar em lote a partir daqui seria o segundo caminho de escrita de status que as
 * quatro camadas do CLAUDE.md existem para impedir.
 *
 * @returns a tela do comitê
 */
export function PaginaCab() {
  const { data: pauta, isPending, isError, error } = usePautaDoComite()

  return (
    <div className="space-y-4">
      <header>
        <h1 className="titulo-da-pagina">{TEXTOS.cab.titulo}</h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.cab.ajuda}</p>
      </header>

      <JanelaDeImplantacao />

      <p className="max-w-prose rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">{TEXTOS.cab.naoDecideAqui}</p>

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={6} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar a pauta" />}

      {pauta && pauta.itens.length === 0 && (
        <EstadoVazio
          icone={<IconeComite className="h-9 w-9" />}
          titulo={TEXTOS.cab.filaVazia.titulo}
          ajuda={TEXTOS.cab.filaVazia.ajuda}
        />
      )}

      {pauta && pauta.itens.length > 0 && (
        <>
          {/* Dito uma vez, no topo, e não repetido em cada linha: se toda solicitação disser "sem
              parecer" porque a integração está desligada, a explicação é do ambiente, não do item. */}
          {!pauta.analiseIaDisponivel && (
            <p className="max-w-prose rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {TEXTOS.cab.iaDesligada}
            </p>
          )}
          {!pauta.revisaoDeCodigo.disponivel && (
            <p className="max-w-prose rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {TEXTOS.apoioAoComite.revisaoDeCodigo}: {pauta.revisaoDeCodigo.motivoIndisponivel}
            </p>
          )}

          {/* Três grupos com cabeçalho e contagem, na ordem da reunião: o que está na mesa, o que
              espera pauta e o que voltou para a contratada. Em lista corrida o presidente lia
              etiqueta por etiqueta para saber onde acabava a mesa. */}
          {ORDEM_NA_REUNIAO.map((status) => {
            const itens = pauta.itens.filter((item) => item.status === status)
            if (itens.length === 0) {
              return null
            }
            return (
              <section key={status} aria-label={ROTULO_STATUS[status]} className="space-y-3">
                <h2 className="text-base font-semibold text-slate-900">
                  {TEXTOS.cab.grupo(ROTULO_STATUS[status] ?? status, itens.length)}
                  {status === 'AGUARDANDO_AJUSTES' && (
                    <span className="ml-2 text-sm font-normal text-slate-500">— {TEXTOS.cab.grupoDevolvidas}</span>
                  )}
                </h2>
                <ul className="space-y-3">
                  {itens.map((item) => (
                    <LinhaDaPauta
                      key={item.id}
                      item={item}
                      iaDesligada={!pauta.analiseIaDisponivel}
                      revisaoDisponivel={pauta.revisaoDeCodigo.disponivel}
                    />
                  ))}
                </ul>
              </section>
            )
          })}
        </>
      )}
    </div>
  )
}
